import { FormikProps } from "formik";
import { MeProps } from "./redux/slices/me/Me";

//#region Z_INDEX_ORDERS
export const CONTENT_HIDDEN_Z_INDEX = -1;
export const BACKGROUND_Z_INDEX = 0;
export const CONTENT_Z_INDEX = 1;
export const OVERLAY_Z_INDEX = 2;
export const DROPDOWN_Z_INDEX = 2;
export const HEADER_Z_INDEX = 3;
export const MODAL_Z_INDEX = 4;
export const DROPDOWN_OVER_MODAL_Z_INDEX = MODAL_Z_INDEX + 1;
export const CAROUSEL_Z_INDEX = MODAL_Z_INDEX + 1;
//#endregion

//#region Constants
export const FADE_ANIMATION_DURATION = 200; //in ms
export const CLICK_OUTSIDE_SET_TIMEOUT_DURATION = 200; //in ms
export const SPRING_ANIMATION_CONFIG = {
  type: "spring",
  stiffness: 800,
  damping: 15,
  mass: 1,
};
export const MOUSE_LEAVE_UPDATION_DELAY = 150; //in ms
export const MOUSE_ENTER_UPDATION_DELAY = 300; //in ms
export const CREATE_POST_MODAL_WIDTH = 726;
export const CREATE_POST_MODAL_WIDTH_WITH_SCHEDULE_POST = 498;
export const CREATE_POST_RICH_TEXT_AREA_HEIGHT = 196;
export const MAX_MODAL_HEIGHT = 90;
export const DEFAULT_FOLLOWERS_FOLLOWING_PAGE_SIZE = 5;
export const DEFAULT_FOLLOWERS_INTRSECTION_INDEX = 3;
export const DEBOUNCE_DELAY = 100; //in ms
export const POST_ANALYSIS_MODAL_WIDTH = 527;
export const DEFAULT_IMAGE_HEIGHT = 250;
export const DEFAULT_IMAGE_TRANSITION_DURATION = 600; //in ms
//#endregion

export const addProxyUrlToProfileImage = (profile_picture: string) => {
  const isGoogleProfilePicture = profile_picture.includes(
    "googleusercontent.com"
  );

  if (!isGoogleProfilePicture) return profile_picture;
  return `https://images.weserv.nl/?url=${profile_picture}`;
};

export const calculateTimePassed = (dateString: string) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (isNaN(diff)) return "";

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years > 0) {
    return `${years} year ago`;
  } else if (months > 0) {
    return `${months} month ago`;
  } else if (days > 0) {
    return `${days}d ago`;
  } else if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return "Just now";
  }
};

export const disableScroll = () => {
  const body = document.getElementsByTagName("body")[0];
  body.style.overflow = "hidden";
};
export const enableScroll = () => {
  const body = document.getElementsByTagName("body")[0];
  body.style.overflow = "auto";
};

export const getPeopleNamesText = (
  peopleNamesData: MeProps.UserWithFollowersMetadata[] | MeProps.User[],
  maxPeopleNamesLength: number = 2
) => {
  if (!peopleNamesData || !peopleNamesData.length) return "";

  const getName = (people: MeProps.UserWithFollowersMetadata | MeProps.User) =>
    `${people.first_name} ${people.last_name}`;

  if (peopleNamesData.length <= maxPeopleNamesLength)
    return peopleNamesData.map(getName).join(" and ");

  const peopleNames = peopleNamesData
    .slice(0, maxPeopleNamesLength)
    .map(getName);
  const additionalCount = peopleNamesData.length - maxPeopleNamesLength;
  const additionalCountText = `+${additionalCount} others`;
  const peopleNamesText = `${peopleNames.join(", ")} ${additionalCountText}`;

  return peopleNamesText;
};

export const isFormSubmitButtonDisabled = ({
  formik,
  requiredFileds,
}: {
  formik: FormikProps<any>;
  requiredFileds: { [key: string]: boolean };
}) => {
  const { values, isValid, errors } = formik;
  if (!isValid || Object.keys(errors).length) {
    return true;
  }

  for (const field of Object.keys(requiredFileds)) {
    if (!values[field]) {
      return requiredFileds[field];
    }
  }
  return false;
};

export const RICHTEXT_INITIAL_VALUE = '<p class="paragraph"><br></p>';

export const validImageFileTypes = ["image/jpeg", "image/png"];
export const validAudioFileTypes = [".mp3", ".wav", ".m4a", ".aac"];
export const validVideoFileTypes = [".mp4", ".mov", ".webm"];

const VALID_FILE_SIZE = 10000000; //10MB

export const validFile = (
  files: FileList,
  validFileType: string[] = validImageFileTypes,
  validFileSize: number = VALID_FILE_SIZE
) => {
  return Array.from(files).filter(
    (file) => validFileType.includes(file.type) && file.size < validFileSize
  );
};

export const getImageBase64 = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const handleLinkClick = (url: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener noreferrer";
  a.target = "_blank";
  a.click();
};

export const MAX_POLL_OPTIONS = 10;

export const default_post_id = "00000000-0000-0000-0000-000000000000";
export const getIsARepost = (EmbedPostID: string) => {
  return !!EmbedPostID && EmbedPostID !== default_post_id;
};

export const removeDuplicates = <T,>(array: T[]): T[] =>
  Array.from(new Set(array));

export const getMentionList = (
  suggestedUsers: MeProps.UserWithFollowersMetadata[]
) => {
  const hasSuggestedUsers = suggestedUsers && suggestedUsers.length;
  if (!hasSuggestedUsers) return [];
  const mentionList = suggestedUsers[0].followers.map((user) => ({
    ...user,
    profile_picture: addProxyUrlToProfileImage(user.profile_picture),
  }));

  return mentionList;
};

export const debounce = (
  callback: () => void,
  delay: number = DEBOUNCE_DELAY
) => {
  let timeoutId: NodeJS.Timeout | null = null;
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      callback();
      timeoutId = null;
    }, delay);
  };
};
export const EMPTY_NEWS_FEED_MESSAGE =
  "No posts found for the selected circles";

export const formatTime = (seconds: number) => {
  const newSeconds = Number(seconds.toFixed());
  const hours = Math.floor(newSeconds / 3600);
  const minutes = Math.floor((newSeconds % 3600) / 60);
  const remainingSeconds = newSeconds % 60;
  return `${hours ? hours.toString().padStart(2, "0") + ":" : ""}${minutes
    .toString()
    .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export enum ARTICLE_CONTENT {
  "text" = 1,
  "image" = 2,
  "audio" = 3,
  "video" = 4,
  "poll" = 5,
  "rating" = 6,
  "embed" = 7,
}

export const formatNumberToK = (num: number): string => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
};
